import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSquareMinus,
  faSquarePlus,
} from "@fortawesome/pro-regular-svg-icons";
import classNames from "classnames";
import orderBy from "lodash/orderBy";

import Badge from "ui/Badge";
import Button from "ui/Button";
import Grid, { type GridColumn, NumberCell } from "ui/Grid";
import { type SortOrder } from "ui/Grid/util";
import {
  ServerStatusType,
  StatusLayerGroupIcon,
  StatusLayerIcon,
} from "ui/Icons";
import Loading from "ui/Loading";
import Panel from "ui/Panel";
import { useQueryStringSort } from "utils/hooks";
import { useRoutes } from "utils/routes";

import {
  OrganizationOverviewServerGroupTableQuery,
  useOrganizationOverviewServerGroupTableQuery,
} from "./gql/Query.generated";

type ServerGroupSummary =
  OrganizationOverviewServerGroupTableQuery["getSummaryStatsByServerGroup"];
type SummaryStat = ServerGroupSummary[number];
type ServerSummary = SummaryStat["servers"][number];

type IssueCounts = Pick<
  SummaryStat,
  "databaseCount" | "warningsCount" | "criticalsCount"
>;
type ServerGroupCounts = IssueCounts & Pick<SummaryStat, "serverCount">;

type GroupRow = ServerGroupCounts & {
  kind: "group";
  rowKey: string;
  serverGroupId: string;
  clusterId: string | null;
  serverGroupName: string;
  primaryServerHumanId: string | null;
  isExpanded: boolean;
  servers: ServerSummary[];
};

type ServerRow = IssueCounts & {
  kind: "server";
  rowKey: string;
  serverGroupId: null;
  serverGroupName: null;
  primaryServerHumanId: null;
  serverCount: null;
  serverHumanId: string;
  serverName: string;
  isPrimary: boolean | null;
  isLastChild: boolean;
  hasRecentSnapshot: boolean;
};

type RowData = GroupRow | ServerRow;

const ServerGroupTable = ({
  organizationSlug,
}: {
  organizationSlug: string;
}) => {
  const { data, loading, error } = useOrganizationOverviewServerGroupTableQuery(
    { variables: { organizationSlug } },
  );
  if (loading || error) {
    return <Loading error={!!error} />;
  }
  return <TableContent data={data?.getSummaryStatsByServerGroup || []} />;
};

const TableContent = ({ data }: { data: ServerGroupSummary }) => {
  const [sort, setSort] = useQueryStringSort<keyof GroupRow>({
    param: "sort",
    sortBy: "serverGroupName",
    sortDirection: "ASC",
  });
  const hasActiveGroups = data.some(
    (s) => !isGroupStale(s) && s.serverCount >= 1,
  );
  const [showInactive, setShowInactive] = useState(!hasActiveGroups);
  const visibleData = useMemo(
    () => (showInactive ? data : data.filter((s) => !isGroupStale(s))),
    [data, showInactive],
  );
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const toggleExpand = (serverGroupId: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(serverGroupId)) {
        next.delete(serverGroupId);
      } else {
        next.add(serverGroupId);
      }
      return next;
    });
  };

  const rows: RowData[] = useMemo(() => {
    const groupRows: GroupRow[] = visibleData.map(
      (summary: SummaryStat): GroupRow => {
        const { serverGroup } = summary;
        return {
          kind: "group",
          rowKey: `group:${serverGroup.id}`,
          serverGroupId: serverGroup.id,
          clusterId: summary.clusterId ?? null,
          serverGroupName: serverGroup.name,
          primaryServerHumanId: serverGroup.primaryServerHumanId ?? null,
          serverCount: summary.serverCount,
          databaseCount: summary.databaseCount,
          warningsCount: summary.warningsCount,
          criticalsCount: summary.criticalsCount,
          isExpanded: !collapsed.has(serverGroup.id),
          servers: summary.servers,
        };
      },
    );
    const order = sort.sortDirection === "ASC" ? "asc" : "desc";
    const sortedGroups =
      sort.sortBy === "serverGroupName"
        ? orderBy(groupRows, [sort.sortBy], [order])
        : orderBy(groupRows, [sort.sortBy, "serverGroupName"], [order, "asc"]);
    return sortedGroups.flatMap((group): RowData[] => {
      if (!group.isExpanded) {
        return [group];
      }
      const sortedServers = sortPrimaryFirst(group.servers);
      return [
        group,
        ...sortedServers.map((s, i) =>
          toServerRow(group, s, i === sortedServers.length - 1),
        ),
      ];
    });
  }, [visibleData, collapsed, sort.sortBy, sort.sortDirection]);

  const handleSort = (field: keyof RowData, order: SortOrder) => {
    setSort({ sortBy: field as keyof GroupRow, sortDirection: order });
  };

  const columns: GridColumn<RowData, keyof RowData>[] = [
    {
      field: "rowKey",
      header: "",
      width: "24px",
      disableSort: true,
      renderer: function CaretCell({ rowData }) {
        if (rowData.kind !== "group") {
          return null;
        }
        if ((rowData.serverCount ?? 0) < 1) {
          return null;
        }
        return (
          <span className="relative inline-flex items-center">
            <Button
              bare
              className="translate-y-[-1px]"
              aria-label={`Expand ${rowData.serverGroupName}`}
              aria-expanded={rowData.isExpanded}
              onClick={() => toggleExpand(rowData.serverGroupId)}
            >
              <FontAwesomeIcon
                icon={rowData.isExpanded ? faSquareMinus : faSquarePlus}
                className="text-gray-500 text-[10px]"
              />
            </Button>
            {/* Vertical tree line that connects the group to its members */}
            {rowData.isExpanded && (
              <span className="absolute left-[6px] top-full bottom-[-5px] w-px bg-[#CCC]" />
            )}
          </span>
        );
      },
    },
    {
      field: "serverGroupName",
      header: "Server Group",
      width: "minmax(30%,1fr)",
      renderer: NameCell,
      // Allow the server row's tree connector lines to render outside the cell's bounds.
      className: ({ rowData }) =>
        rowData.kind === "server" ? "!overflow-visible" : "",
    },
    {
      field: "databaseCount",
      header: "# of Databases",
      width: "minmax(10%,140px)",
      style: "number",
      defaultSortOrder: "DESC",
      nullValue: "",
      renderer: NumberCell,
    },
    {
      field: "warningsCount",
      header: "Warnings",
      width: "minmax(10%,140px)",
      style: "number",
      defaultSortOrder: "DESC",
      nullValue: "",
      renderer: NumberCell,
    },
    {
      field: "criticalsCount",
      header: "Criticals",
      width: "minmax(10%,140px)",
      style: "number",
      defaultSortOrder: "DESC",
      nullValue: "",
      renderer: NumberCell,
    },
  ];

  const titleSearch = (
    <div className="flex">
      <input
        type="checkbox"
        checked={showInactive}
        id="show_inactive_server_groups"
        onChange={(evt) => setShowInactive(evt.target.checked)}
      />
      <label htmlFor="show_inactive_server_groups">
        Show server groups with no recent statistics data
      </label>
    </div>
  );

  return (
    <Panel title="All Server Groups" secondaryTitle={titleSearch}>
      <Grid
        striped
        data={rows}
        columns={columns}
        sortedBy={sort.sortBy as keyof RowData}
        sortedOrder={sort.sortDirection}
        handleSort={handleSort}
      />
    </Panel>
  );
};

function NameCell({ rowData }: { rowData: RowData }) {
  const { server, serverGroupServers, serverGroupEdit } = useRoutes();
  switch (rowData.kind) {
    case "group": {
      const groupLink =
        rowData.clusterId == null
          ? serverGroupEdit(rowData.serverGroupId)
          : serverGroupServers(rowData.serverGroupId);
      return (
        <span className="flex items-center">
          <StatusLayerGroupIcon status={groupStatus(rowData.servers)} />
          {rowData.clusterId != null && rowData.servers.length === 0 ? (
            <span className="ml-[4px]">{rowData.serverGroupName}</span>
          ) : (
            <Link className="ml-[4px]" to={groupLink}>
              {rowData.serverGroupName}
            </Link>
          )}
        </span>
      );
    }
    case "server":
      return (
        <span className="flex items-center">
          <span className="relative w-[12px] mr-[6px] shrink-0 self-stretch">
            <span
              className={classNames(
                "absolute left-[-18px] w-px bg-[#CCC]",
                rowData.isLastChild
                  ? "top-[-5px] h-[calc(50%+5px)]"
                  : "top-[-5px] bottom-[-5px]",
              )}
            />
            <span className="absolute left-[-18px] top-1/2 right-1 h-px bg-[#CCC]" />
          </span>
          <StatusLayerIcon status={serverStatus(rowData)} />
          <Link className="ml-[4px]" to={server(rowData.serverHumanId)}>
            {rowData.serverName}
          </Link>
          {rowData.isPrimary != null && (
            <Badge
              className={classNames(
                "ml-[6px] border font-medium",
                !rowData.isPrimary &&
                  "!bg-[#f0f0f0] !text-[#555] !border-[#ddd]",
              )}
            >
              {rowData.isPrimary ? "primary" : "replica"}
            </Badge>
          )}
        </span>
      );
  }
}

function isGroupStale(summary: SummaryStat): boolean {
  if (summary.servers.length === 0) {
    return true;
  }
  return summary.servers.every((s) => s.latestStats == null);
}

function sortPrimaryFirst(servers: ServerSummary[]): ServerSummary[] {
  return [...servers].sort((a, b) => {
    const ap = a.latestStats?.isPrimary ? 0 : 1;
    const bp = b.latestStats?.isPrimary ? 0 : 1;
    if (ap !== bp) {
      return ap - bp;
    }
    return a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase());
  });
}

function toServerRow(
  parent: GroupRow,
  server: ServerSummary,
  isLastChild: boolean,
): ServerRow {
  return {
    kind: "server",
    rowKey: `server:${parent.serverGroupId}:${server.id}`,
    serverGroupId: null,
    serverGroupName: null,
    primaryServerHumanId: null,
    serverCount: null,
    databaseCount: server.databaseCount,
    warningsCount: server.warningsCount,
    criticalsCount: server.criticalsCount,
    serverHumanId: server.humanId,
    serverName: server.name,
    isPrimary: server.latestStats?.isPrimary ?? null,
    isLastChild,
    hasRecentSnapshot: server.latestStats != null,
  };
}

function groupStatus(servers: ServerSummary[]): ServerStatusType {
  if (servers.some((s) => s.latestStats != null && s.criticalsCount > 0)) {
    return "critical";
  }
  if (servers.length > 0 && servers.every((s) => s.latestStats != null)) {
    return "okay";
  }
  return "no data";
}

function serverStatus(row: ServerRow): ServerStatusType {
  if (!row.hasRecentSnapshot) {
    return "no data";
  }
  return row.criticalsCount > 0 ? "critical" : "okay";
}

export default ServerGroupTable;
